import { useEffect, useState } from 'react';

// Global image cache with configurable expiration
const imageCache = new Map();



// Cache configurations
const CACHE_CONFIGS = {
  DISABLED: {
    enabled: false,
    duration: 0
  },
  DEFAULT: {
    enabled: true,
    duration: 60 * 1000
  },
  VERY_LONG: {
    enabled: true,
    duration: 20 * 60 * 1000
  }
};

/**
 * Get cache configuration for rolling displays
 * Rolling displays benefit from longer caching since the same images appear frequently
 * @param {boolean} isRolling - Whether the display is rolling
 * @returns {object} Cache configuration object
 */
export const getRollingCacheConfig = (isRolling = false) => {
  return isRolling ? CACHE_CONFIGS.VERY_LONG : CACHE_CONFIGS.DISABLED;
};

// Cache management functions
const getCachedImage = (src, cacheConfig) => {
  if (!cacheConfig.enabled) return null;
  
  const cached = imageCache.get(src);
  if (cached && Date.now() - cached.timestamp < cacheConfig.duration) {
    return cached.image;
  }
  return null;
};

const setCachedImage = (src, image, cacheConfig) => {
  if (!cacheConfig.enabled) return;
  
  imageCache.set(src, {
    image,
    timestamp: Date.now()
  });
};

const CachedIGImg = (props) => {
  const { 
    useImageProxy = true, 
    cacheConfig = CACHE_CONFIGS.DEFAULT,
    ...restProps 
  } = props;
  
  const src = props.src ?? '/1x1-transparent.png';
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [cachedImage, setCachedImageState] = useState(null);

  // Check cache and reset loading state when src changes
  useEffect(() => {
    if (src) {
      const cached = getCachedImage(src, cacheConfig);
      if (cached) {
        setCachedImageState(cached);
        setIsLoading(false);
        setHasError(false);
      } else {
        setCachedImageState(null);
        setIsLoading(true);
        setHasError(false);
      }
    }
  }, [src, cacheConfig]);

  const handleLoad = (e) => {
    // Cache the loaded image if caching is enabled
    setCachedImage(src, e.target, cacheConfig);
    setCachedImageState(e.target);
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // If we have a cached image, use it directly
  if (cachedImage) {
    return (
      <img
        {...restProps}
        src={cachedImage.src}
        alt={props.alt || ''}
        crossOrigin={useImageProxy ? 'anonymous' : undefined}
        style={props.style}
      />
    );
  }

  return (
    src == null
      ?
        <span className={`inline-block ${props.className ?? ''}`}></span>
      :
        <div className="relative">
          {isLoading && (
            <span className={`inline-block ${props.className ?? ''}`}></span>
          )}
          <img
            {...restProps}
            src={src.startsWith('/') ? src : (useImageProxy ? `${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${src}` : src)} 
            alt={props.alt || ''}
            crossOrigin={useImageProxy ? 'anonymous' : undefined}
            onLoad={handleLoad}
            onError={handleError}
            style={{
              ...props.style,
              display: isLoading ? 'none' : 'block'
            }}
          />
        </div>
  );
};

export default CachedIGImg;
